import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  booleanAttribute,
} from '@angular/core';
import {
  DsIconBadgeComponent,
  type DsIconBadgeShadow,
  type DsIconBadgeSize,
} from '../icons/ds-icon-badge.component';
import type { DsIconName } from '../icons/ds-icons';

export type DsCardBadgeAppearance = 'card-elevated' | 'badge-elevated';
export type DsCardBadgeSize = 's' | 'm' | 'l';
export type DsCardBadgeShadow = 'none' | 'l1' | 'l2' | 'l3';

@Component({
  selector: 'ds-card-badge',
  standalone: true,
  imports: [NgTemplateOutlet, DsIconBadgeComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (href) {
      <a
        [class]="hostClasses"
        [style.--ds-card-badge-resolved-background]="resolvedBackground"
        [style.--ds-card-badge-resolved-border-color]="resolvedBorderColor"
        [style.--ds-card-badge-resolved-text-color]="resolvedTextColor"
        [attr.href]="disabled ? null : href"
        [attr.target]="target || null"
        [attr.rel]="rel || null"
        [attr.tabindex]="disabled ? -1 : null"
        [attr.aria-disabled]="disabled ? 'true' : null"
        [attr.aria-label]="ariaLabel || null"
        (click)="handleActivation($event)"
      >
        <ng-container [ngTemplateOutlet]="cardContent" />
      </a>
    } @else if (isInteractive) {
      <button
        type="button"
        [class]="hostClasses"
        [style.--ds-card-badge-resolved-background]="resolvedBackground"
        [style.--ds-card-badge-resolved-border-color]="resolvedBorderColor"
        [style.--ds-card-badge-resolved-text-color]="resolvedTextColor"
        [disabled]="disabled"
        [attr.aria-label]="ariaLabel || null"
        (click)="handleActivation($event)"
      >
        <ng-container [ngTemplateOutlet]="cardContent" />
      </button>
    } @else {
      <div
        [class]="hostClasses"
        [style.--ds-card-badge-resolved-background]="resolvedBackground"
        [style.--ds-card-badge-resolved-border-color]="resolvedBorderColor"
        [style.--ds-card-badge-resolved-text-color]="resolvedTextColor"
      >
        <ng-container [ngTemplateOutlet]="cardContent" />
      </div>
    }

    <ng-template #cardContent>
      <ds-icon-badge
        [name]="icon"
        [size]="badgeSize"
        [fill]="iconFill"
        [backgroundColor]="resolvedBadgeBackgroundColor"
        [iconColor]="resolvedBadgeIconColor"
        [shadow]="resolvedBadgeShadow"
      />
      <span class="ds-card-badge__title">{{ title }}</span>
    </ng-template>
  `,
  styles: [`
    :host {
      display: block;
      min-width: 0;
    }

    .ds-card-badge {
      appearance: none;
      position: relative;
      display: flex;
      width: 100%;
      min-width: 0;
      flex-direction: column;
      align-items: center;
      justify-content: flex-start;
      margin: 0;
      overflow: hidden;
      background: var(--ds-card-badge-resolved-background);
      border: 0 solid transparent;
      border-radius: var(--ds-card-badge-border-radius);
      box-sizing: border-box;
      color: var(--ds-card-badge-resolved-text-color);
      font-family: var(--ds-foundation-font-family-primary, 'Sura Sans', system-ui, sans-serif);
      text-align: center;
      text-decoration: none;
      transform: scale(1);
      transform-origin: center;
      outline: none;
      transition:
        transform var(--ds-card-badge-transition-duration) ease,
        opacity var(--ds-card-badge-transition-duration) ease,
        box-shadow var(--ds-card-badge-transition-duration) ease;
    }

    .ds-card-badge::before {
      content: '';
      position: absolute;
      inset: 0;
      z-index: 0;
      border-radius: inherit;
      background: transparent;
      pointer-events: none;
      transition: background-color var(--ds-card-badge-transition-duration) ease;
    }

    .ds-card-badge > ds-icon-badge,
    .ds-card-badge__title {
      position: relative;
      z-index: 1;
    }

    .ds-card-badge--size-s {
      padding: var(--ds-card-badge-s-padding);
      gap: var(--ds-card-badge-s-gap);
    }

    .ds-card-badge--size-m {
      padding: var(--ds-card-badge-m-padding);
      gap: var(--ds-card-badge-m-gap);
    }

    .ds-card-badge--size-l {
      padding: var(--ds-card-badge-l-padding);
      gap: var(--ds-card-badge-l-gap);
    }

    .ds-card-badge--bordered {
      border-width: var(--ds-card-badge-border-width);
      border-color: var(--ds-card-badge-resolved-border-color);
    }

    .ds-card-badge--shadow-none { box-shadow: none; }
    .ds-card-badge--shadow-l1 { box-shadow: var(--ds-card-badge-shadow-l1); }
    .ds-card-badge--shadow-l2 { box-shadow: var(--ds-card-badge-shadow-l2); }
    .ds-card-badge--shadow-l3 { box-shadow: var(--ds-card-badge-shadow-l3); }

    .ds-card-badge--interactive {
      cursor: pointer;
    }

    .ds-card-badge--interactive:hover:not(.ds-card-badge--disabled),
    .ds-card-badge--interactive:active:not(.ds-card-badge--disabled) {
      transform: scale(var(--ds-card-badge-interaction-scale));
    }

    .ds-card-badge--interactive:hover:not(.ds-card-badge--disabled)::before {
      background: var(--ds-card-badge-hover-overlay);
    }

    .ds-card-badge--interactive:active:not(.ds-card-badge--disabled)::before {
      background: var(--ds-card-badge-pressed-overlay);
    }

    .ds-card-badge--interactive:focus-visible:not(.ds-card-badge--disabled) {
      outline: var(--ds-focus-stroke) solid var(--ds-focus-color);
      outline-offset: var(--ds-focus-gap);
    }

    .ds-card-badge--disabled {
      opacity: calc(var(--ds-card-badge-disabled-opacity) / 100px);
      cursor: not-allowed;
    }

    .ds-card-badge__title {
      display: -webkit-box;
      min-width: 100%;
      overflow: hidden;
      color: var(--ds-card-badge-resolved-text-color);
      font-weight: var(--ds-card-badge-font-weight);
      overflow-wrap: anywhere;
      text-overflow: ellipsis;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;
    }

    .ds-card-badge--size-s .ds-card-badge__title {
      font-size: var(--ds-card-badge-s-font-size);
      line-height: var(--ds-card-badge-s-line-height);
    }

    .ds-card-badge--size-m .ds-card-badge__title {
      font-size: var(--ds-card-badge-m-font-size);
      line-height: var(--ds-card-badge-m-line-height);
    }

    .ds-card-badge--size-l .ds-card-badge__title {
      font-size: var(--ds-card-badge-l-font-size);
      line-height: var(--ds-card-badge-l-line-height);
    }
  `],
})
export class DsCardBadgeComponent {
  @Input({ required: true }) title = '';
  @Input() icon: DsIconName | string = 'Favorite';
  @Input({ transform: booleanAttribute }) iconFill = false;
  @Input() size: DsCardBadgeSize = 'm';
  @Input() appearance: DsCardBadgeAppearance = 'card-elevated';
  @Input({ transform: booleanAttribute }) clickable = false;
  @Input({ transform: booleanAttribute }) disabled = false;
  @Input() href = '';
  @Input() target = '';
  @Input() rel = '';
  @Input() ariaLabel = '';
  @Input() background: string | null = null;
  @Input() border: boolean | string = false;
  @Input() borderColor: string | null = null;
  @Input() shadow: DsCardBadgeShadow | null = null;
  @Input() textColor: string | null = null;
  @Input() badgeBackgroundColor: string | null = null;
  @Input() badgeIconColor: string | null = null;
  @Input() badgeShadow: DsIconBadgeShadow | null = null;

  @Output() clicked = new EventEmitter<MouseEvent>();

  get isInteractive(): boolean {
    return !!this.href || this.clickable || this.clicked.observers.length > 0;
  }

  get resolvedBackground(): string {
    return this.background ?? 'var(--ds-card-badge-background)';
  }

  get resolvedBorderColor(): string {
    return this.borderColor ?? 'var(--ds-card-badge-border-color)';
  }

  get resolvedTextColor(): string {
    return this.textColor ?? 'var(--ds-card-badge-text-color)';
  }

  get resolvedBadgeBackgroundColor(): string {
    if (this.badgeBackgroundColor) return this.badgeBackgroundColor;
    return this.appearance === 'badge-elevated'
      ? 'var(--ds-card-badge-badge-elevated-background)'
      : 'var(--ds-card-badge-badge-background)';
  }

  get resolvedBadgeIconColor(): string {
    return this.badgeIconColor ?? 'var(--ds-card-badge-badge-icon-color)';
  }

  get resolvedCardShadow(): DsCardBadgeShadow {
    return this.shadow ?? (this.appearance === 'card-elevated' ? 'l2' : 'none');
  }

  get resolvedBadgeShadow(): DsIconBadgeShadow {
    return this.badgeShadow ?? (this.appearance === 'badge-elevated' ? 'l2' : 'none');
  }

  get badgeSize(): DsIconBadgeSize {
    const sizes: Record<DsCardBadgeSize, DsIconBadgeSize> = { s: 44, m: 48, l: 56 };
    return sizes[this.size];
  }

  get hasBorder(): boolean {
    return this.border === true || this.border === '' || this.border === 'true';
  }

  get hostClasses(): string {
    return [
      'ds-card-badge',
      `ds-card-badge--${this.appearance}`,
      `ds-card-badge--size-${this.size}`,
      `ds-card-badge--shadow-${this.resolvedCardShadow}`,
      this.hasBorder ? 'ds-card-badge--bordered' : 'ds-card-badge--borderless',
      this.isInteractive ? 'ds-card-badge--interactive' : '',
      this.disabled ? 'ds-card-badge--disabled' : '',
    ].filter(Boolean).join(' ');
  }

  handleActivation(event: MouseEvent): void {
    if (this.disabled) {
      event.preventDefault();
      return;
    }
    this.clicked.emit(event);
  }
}
